import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';
import { BeneficiariService } from '../../../beneficiari.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { notDefaultValidator } from 'src/app/validators/not-default';
import { Beneficiar, Localizare } from '../../../beneficiari.interface';
import { AdreseCentreService } from 'src/app/platform/nomenclatoare/activities-setari/adrese-centre/adrese-centre.service';
import { CommonService } from 'src/app/services/common.service';
import { dateRangeValidator } from 'src/app/validators/date-period';
import { formatDate } from '@angular/common';


@Component({
    selector: 'app-edit-location',
    templateUrl: './edit-location.component.html',
    styleUrls: ['./edit-location.component.scss']
})
export class EditLocationComponent {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;

    localizare?: Localizare;

    situationType = [
        { name: '--Alegeți--', id: -1 },
        { name: 'În plasament', id: 1 },
        { name: 'Adresă domiciliu', id: 2 },
        { name: 'Reședință', id: 3 },
        { name: 'Fără forme legale', id: 4 },
    ]

    form: FormGroup = new FormGroup({
        situation_type: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        date_from: new FormControl(null, [Validators.required]),
        date_to: new FormControl(null, []),
        // address_type: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        location_dgaspc: new FormControl(-1, []),
        observatii: new FormControl('', []),

        tara_id: new FormControl(1, []), // Id Romania default!
        tara_nume: new FormControl('Romania', []),
        judet_id: new FormControl(-1, []),
        judet_nume: new FormControl('', []),
        localitate_id: new FormControl(-1, []),
        localitate_nume: new FormControl('', []),
        sector: new FormControl(0, []),
        strada_id: new FormControl(-1, []),
        strada_nume: new FormControl('', []),
        numar: new FormControl('', []),
        bloc: new FormControl('', []),
        etaj: new FormControl('', []),
        scara: new FormControl('', []),
        apartament: new FormControl('', []),
        cod_postal: new FormControl('', []),
    }, { validators: dateRangeValidator('date_from', 'date_to') });

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    adreseCentre: Generic[] = [];

    private destroy$ = new Subject<void>();

    tari: Generic[] = [];
    filteredTari: any[] = [];
    tariControl = new FormControl();

    judete: Generic[] = [];
    filteredJudete: any[] = [];
    judeteControl = new FormControl();

    localitati: Generic[] = [];
    localitatiControl = new FormControl();
    isLocalitatiLoading = false;
    showLocalitatiMessage = true;
    localitatiLoadComplete = false;

    strazi: Generic[] = [];
    straziControl = new FormControl();
    isStraziLoading = false;
    showStraziMessage = true;
    straziLoadComplete = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { beneficiar: Beneficiar, row_id: number },
        private dialogRef: MatDialogRef<EditLocationComponent>,
        private beneficiariService: BeneficiariService,
        private adreseCentreService: AdreseCentreService,
        private commonService: CommonService,
        private notificationService: NotificationService,
    ) {
        this.localizare = this.data.beneficiar.localizari.find(item => item.id === this.data.row_id);
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit() {
        this.loadAdreseCentre();
        this.loadTari();
        this.loadJudete(1);

        this.form.get('tara_id')?.disable();

        this.renderFieldsSearch();

        if (this.localizare) {
            this.form.controls['situation_type'].patchValue(this.localizare.situation_type_id || -1);
            this.form.controls['date_from'].patchValue(this.localizare.date_from || null);
            this.form.controls['date_to'].patchValue(this.localizare.date_to || null);
            // this.form.controls['address_type'].patchValue(this.localizare.address_type || -1);
            this.form.controls['location_dgaspc'].patchValue(this.localizare.adresa_centru_id || -1);
            this.form.controls['observatii'].patchValue(this.localizare.observatii);

            this.changeAddressType();

            if (this.localizare.adresa) {
                this.form.controls['tara_id'].patchValue(this.localizare.adresa.tara_id || -1);
                this.form.controls['tara_nume'].patchValue(this.localizare.adresa.tara_nume);
                this.form.controls['judet_id'].patchValue(this.localizare.adresa.judet_id || -1);
                this.form.controls['judet_nume'].patchValue(this.localizare.adresa.judet_nume);
                this.form.controls['localitate_id'].patchValue(this.localizare.adresa.localitate_id || -1);
                this.form.controls['localitate_nume'].patchValue(this.localizare.adresa.localitate_nume);
                this.form.controls['sector'].patchValue(this.localizare.adresa.sector || 0);
                this.form.controls['strada_id'].patchValue(this.localizare.adresa.strada_id || -1);
                this.form.controls['strada_nume'].patchValue(this.localizare.adresa.strada_nume);
                this.form.controls['numar'].patchValue(this.localizare.adresa.numar);
                this.form.controls['bloc'].patchValue(this.localizare.adresa.bloc);
                this.form.controls['etaj'].patchValue(this.localizare.adresa.etaj);
                this.form.controls['scara'].patchValue(this.localizare.adresa.scara);
                this.form.controls['apartament'].patchValue(this.localizare.adresa.apartament);
                this.form.controls['cod_postal'].patchValue(this.localizare.adresa.cod_postal);

                this.fetchLocalitati(this.localizare.adresa.localitate_nume);
                this.fetchStrazi(this.localizare.adresa.strada_nume);
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadAdreseCentre() {
        this.isLoading = true;
        this.adreseCentreService
            .getListActive()
            .pipe(
                map((results: any) => [...results.data.map((item: any) => {
                    return {
                        id: item.id,
                        name: item.web_name
                    }
                })]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.adreseCentre = results;
                },
                error: (error) => {
                    console.error('Error loading adrese centre', error);
                },
            });
    }

    renderFieldsSearch() {
        this.tariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTari = this.tari;
                    return of(this.tari);
                }
                return this.filterTari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.judeteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredJudete = this.judete;
                    return of(this.judete);
                }
                return this.filterJudete(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredJudete = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.localitatiControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(localitate => {
                this.fetchLocalitati(localitate);
                this.showLocalitatiMessage = true;
                this.localitatiLoadComplete = false;
            });

        this.straziControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(localitate => {
                this.fetchStrazi(localitate);
                this.showStraziMessage = true;
                this.straziLoadComplete = false;
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterTari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tari.filter(tara =>
            this.normalizeString(tara.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadTari() {
        this.isLoading = true;
        this.commonService
            .getTariActive()
            .pipe(
                map((resp: Generic[]) => [...resp]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.tari = results;
                    this.filteredTari = [...this.tari];
                },
                error: (error) => {
                    console.error('Error loading tari', error);
                    this.filteredTari = [];
                },
            });
    }

    onTariSelectClosed() {
        const selectedValue = this.tariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTari.some(tara =>
                tara.id === this.form.get('tara_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tariControl.setValue(null);
                this.filteredTari = this.tari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onTariChange(taraId: number): void {
        this.form.controls['judet_id'].patchValue(-1);
        this.form.controls['localitate_id'].patchValue(-1);
        this.form.controls['strada_id'].patchValue(-1);

        if (taraId == -1) {
            this.judete = [];
            this.filteredJudete = [];

            this.localitati = [];
            this.strazi = [];
            return
        }

        if (taraId) {
            this.loadJudete(taraId);

            const selectedTara = this.tari.find(tara => tara.id === taraId);

            if (selectedTara) {
                this.form.get('tara_nume')?.patchValue(selectedTara.name);
            }
        }
    }

    loadJudete(taraId: number) {
        this.isLoading = true;
        this.commonService
            .getJudeteActiveSearch(taraId)
            .pipe(
                map((resp: Generic[]) => [...resp]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (results) => {
                    this.judete = [...results];
                    this.filteredJudete = [...results];
                },
                error: (error) => {
                    console.log('Error loading judete', error);
                    this.filteredJudete = [];
                }
            });
    }

    onJudeteChange(judetId: number) {
        this.form.controls['localitate_id'].patchValue(-1);
        this.localitatiControl.patchValue('');
        this.localitati = [];
        this.showLocalitatiMessage = true;
        this.localitatiLoadComplete = false;

        this.form.controls['strada_id'].patchValue(-1);
        this.straziControl.patchValue('');
        this.strazi = [];
        this.showStraziMessage = true;
        this.straziLoadComplete = false;

        if (judetId) {
            const selectedJudet = this.judete.find(judet => judet.id === judetId);

            if (selectedJudet) {
                this.form.get('judet_nume')?.patchValue(selectedJudet.name);
            }
        }
    }

    onJudeteSelectClosed() {
        const selectedValue = this.judeteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredJudete.some(judet =>
                judet.id === this.form.get('judet_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.judeteControl.setValue(null);
                this.filteredJudete = this.judete;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterJudete(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.judete.filter(judet =>
            this.normalizeString(judet.name).includes(normalizedSearchTerm)
        ));
    }

    fetchLocalitati(localitate: string) {
        const taraValue = this.form.get('tara_id')?.value;
        const judetValue = this.form.get('judet_id')?.value;
        const tara = taraValue && taraValue !== -1 ? this.form.get('tara_id')?.value : undefined;
        const judet = judetValue && judetValue !== -1 ? this.form.get('judet_id')?.value : undefined;

        if (localitate && localitate.length >= 2) {
            this.isLocalitatiLoading = true;
            this.commonService.getLocalitatiActive(tara, judet, localitate).subscribe({
                next: (response: any) => {
                    this.localitati = response;
                    this.isLocalitatiLoading = false;
                    this.localitatiLoadComplete = true;
                    this.showLocalitatiMessage = this.localitati.length === 0;
                },
                error: (err: any) => {
                    this.isLocalitatiLoading = false;
                    this.showLocalitatiMessage = true;
                    this.localitatiLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.localitati = [];
            this.showLocalitatiMessage = true;
            this.localitatiLoadComplete = false;
        }
    }

    onLocalitatiChange(localitateId: number) {
        this.form.controls['strada_id'].patchValue(-1);
        this.straziControl.patchValue('');
        this.strazi = [];
        this.showStraziMessage = true;
        this.straziLoadComplete = false;

        if (localitateId) {
            const selectedLocalitate = this.localitati.find(localitate => localitate.id === localitateId);

            if (selectedLocalitate) {
                this.form.get('localitate_nume')?.patchValue(selectedLocalitate.name);
            }
        }
    }

    fetchStrazi(strada: string) {
        const taraValue = this.form.get('tara_id')?.value;
        const judetValue = this.form.get('judet_id')?.value;
        const localitateEmitentValue = this.form.get('localitate_id')?.value;
        const tara = taraValue && taraValue !== -1 ? this.form.get('tara_id')?.value : undefined;
        const judet = judetValue && judetValue !== -1 ? this.form.get('judet_id')?.value : undefined;
        const localitate = localitateEmitentValue && localitateEmitentValue !== -1 ? localitateEmitentValue : undefined;

        if (strada && strada.length >= 2) {
            this.isStraziLoading = true;
            this.commonService.getStraziActive(tara, judet, localitate, strada).subscribe({
                next: (response: any) => {
                    this.strazi = response;
                    this.isStraziLoading = false;
                    this.straziLoadComplete = true;
                    this.showStraziMessage = this.strazi.length === 0;
                },
                error: (err: any) => {
                    this.isStraziLoading = false;
                    this.showStraziMessage = true;
                    this.straziLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.strazi = [];
            this.showStraziMessage = true;
            this.straziLoadComplete = false;
        }
    }

    onStraziChange(stradaId: number) {
        if (stradaId) {
            const selectedStrada = this.strazi.find(strada => strada.id === stradaId);

            if (selectedStrada) {
                this.form.get('strada_nume')?.patchValue(selectedStrada.name);
            }
        }
    }

    changeAddressType() {
        const location_dgaspc = this.form.get('location_dgaspc') as FormControl;
        const tara_id = this.form.get('tara_id') as FormControl;
        const judet_id = this.form.get('judet_id') as FormControl;
        const localitate_id = this.form.get('localitate_id') as FormControl;
        const sector = this.form.get('sector') as FormControl;
        const strada_id = this.form.get('strada_id') as FormControl;
        const numar = this.form.get('numar') as FormControl;
        const bloc = this.form.get('bloc') as FormControl;
        const etaj = this.form.get('etaj') as FormControl;
        const scara = this.form.get('scara') as FormControl;
        const apartament = this.form.get('apartament') as FormControl;
        const cod_postal = this.form.get('cod_postal') as FormControl;

        const tara_nume = this.form.get('tara_nume') as FormControl;
        const judet_nume = this.form.get('judet_nume') as FormControl;
        const localitate_nume = this.form.get('localitate_nume') as FormControl;
        const strada_nume = this.form.get('strada_nume') as FormControl;

        if (
            this.form.get('situation_type')?.value === 1
        ) {
            location_dgaspc.setValidators([Validators.required, notDefaultValidator('-1')]);

            // tara_id.patchValue(1);
            tara_id.clearValidators();
            tara_id.setErrors(null);
            tara_id.markAsUntouched();
            judet_id.patchValue(-1);
            judet_id.clearValidators();
            judet_id.setErrors(null);
            judet_id.markAsUntouched();
            localitate_id.patchValue(-1);
            localitate_id.clearValidators();
            localitate_id.setErrors(null);
            localitate_id.markAsUntouched();
            strada_id.patchValue(-1);
            strada_id.clearValidators();
            strada_id.setErrors(null);
            strada_id.markAsUntouched();
            numar.patchValue('');
            numar.clearValidators();
            numar.setErrors(null);
            numar.markAsUntouched();
            sector.patchValue(0);
            bloc.patchValue('');
            etaj.patchValue('');
            scara.patchValue('');
            apartament.patchValue('');
            cod_postal.patchValue('');

            // tara_nume.patchValue('Romania');
            judet_nume.patchValue('');
            localitate_nume.patchValue('');
            strada_nume.patchValue('');
        } else {
            tara_id.setValidators([Validators.required, notDefaultValidator('-1')]);
            judet_id.setValidators([Validators.required, notDefaultValidator('-1')]);
            localitate_id.setValidators([Validators.required, notDefaultValidator('-1')]);
            strada_id.setValidators([Validators.required, notDefaultValidator('-1')]);
            numar.setValidators([Validators.required]);

            location_dgaspc.patchValue(-1);
            location_dgaspc.clearValidators();
            location_dgaspc.setErrors(null);
            location_dgaspc.markAsUntouched();
        }

        this.form.updateValueAndValidity();
    }

    submitForm() {
        this.isLoading = true;

        const date_from = this.form.value.date_from;
        const date_to = this.form.value.date_to;

        const payload = {
            ...this.form.value,
            date_from: date_from ? formatDate(date_from, 'yyyy-MM-dd', 'en-US') : '',
            date_to: date_to ? formatDate(date_to, 'yyyy-MM-dd', 'en-US') : '',
            tara_id: 1,
        };

        // console.log('Payload edit location: ', payload)
        this.beneficiariService
            .updateLocalizare(this.data.row_id, payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
